import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import dayjs from "dayjs";
import { Users, Search } from "lucide-react";

// 2E · People unified view (Lean-Core Pass 2)
// Read-only aggregator across 4 relationship collections. Provenance preserved.
const ROLE_STYLE = {
  CONTACT:           "bg-forest/10 text-forest border-forest/30",
  VOLUNTEER:         "bg-terracotta/10 text-terracotta border-terracotta/30",
  RETREAT_APPLICANT: "bg-bone text-ink border-line",
  PARTNER:           "bg-ink/10 text-ink border-ink/20",
};

export default function People() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("ALL");

  useEffect(() => {
    api.get(`/people${q ? `?q=${encodeURIComponent(q)}` : ""}`).then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || e.message));
  }, [q]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (role === "ALL") return data.items;
    return data.items.filter(x => x.role === role);
  }, [data, role]);

  if (err) return <div className="p-8 text-red-700" data-testid="people-error">People view failed: {err}</div>;
  if (!data) return <div className="p-8 text-forest/60" data-testid="people-loading">Loading People…</div>;

  const roleCounts = data.counts_by_role || {};

  return (
    <div className="space-y-6" data-testid="people-page">
      <header>
        <div className="overline">Command Surface</div>
        <h1 className="font-serif text-4xl mt-1">People</h1>
        <p className="text-ink-soft text-sm">Unified relationship view · provenance preserved · storage NOT merged</p>
      </header>

      <section className="bg-admin-surface border border-line rounded-xl p-4 flex flex-wrap gap-3 items-center" data-testid="people-filters">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="h-4 w-4 text-ink-soft" strokeWidth={1.5} />
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name or email…"
                 className="flex-1 bg-transparent border-b border-line focus:border-forest outline-none text-sm py-1"
                 data-testid="people-search-input" />
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {["ALL", "CONTACT", "VOLUNTEER", "RETREAT_APPLICANT", "PARTNER"].map(r => (
            <button key={r} onClick={() => setRole(r)} data-testid={`people-role-${r.toLowerCase()}`}
                    className={`px-2 py-1 rounded border ${role === r ? "bg-forest text-bone border-forest" : "border-line hover:bg-bone"}`}>
              {r} {r !== "ALL" && `(${roleCounts[r] ?? 0})`}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid="people-list">
        <div className="px-5 py-3 border-b border-line bg-bone/40 flex items-center justify-between">
          <div className="overline">People · {filtered.length} shown / {data.count} total</div>
          <div className="text-[10px] text-ink-soft">{Object.entries(roleCounts).map(([k, v]) => `${k}:${v}`).join(" · ")}</div>
        </div>
        {filtered.length === 0
          ? <div className="p-6 text-sm text-ink-soft italic">No matches.</div>
          : <ul className="divide-y divide-line">
              {filtered.map((p, i) => (
                <li key={`${p.source}-${p.id || i}`} className="px-5 py-3 flex items-start gap-3 hover:bg-bone/40" data-testid={`person-row-${p.source}-${p.id || i}`}>
                  <div className="shrink-0 w-8 h-8 rounded-full bg-bone border border-line grid place-items-center text-xs text-forest">
                    {(p.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{p.name}</span>
                      <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${ROLE_STYLE[p.role] || "border-line"}`}>{p.role}</span>
                      {p.status && <span className="text-[10px] text-ink-soft">· {p.status}</span>}
                    </div>
                    <div className="text-xs text-ink-soft truncate">
                      {p.email || "—"} {p.country ? `· ${p.country}` : ""} {p.category ? `· ${p.category}` : ""}
                    </div>
                  </div>
                  <div className="text-right text-xs text-ink-soft">
                    <div>{p.created_at ? dayjs(p.created_at).format("MMM D, YYYY") : ""}</div>
                    <div className="text-[10px]">source: {p.source}</div>
                  </div>
                </li>
              ))}
            </ul>}
      </section>

      <div className="text-xs text-ink-soft italic" data-testid="people-note">{data.note}</div>
    </div>
  );
}
